import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { canvaService } from '@/services/canva.service';
import {
  CanvaImportRequest,
  ListDesignsParams,
} from '@/types/canva';
import { toast } from '@/utils/toast';
import { useTranslation } from 'react-i18next';
import { formatApiError } from '@/services/api';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { teamQueryKeys } from '@/lib/queryKeys';

/**
 * React Query hooks for Canva import
 */

// Query keys for cache management
// channels is a function so the team ID is embedded, preventing God-Mode stale-cache issues.
export const CANVA_KEYS = {
  channels: (teamId: number | null, includeInactive = false) =>
    ['canva-channels', teamId, { includeInactive }] as const,
  designs: (params: { channelId: number; search?: string; sortBy?: string }) =>
    ['canva-designs', params] as const,
  importStatus: (jobId: number) => ['canva-import-status', jobId] as const,
};

/**
 * Hook to get connected Canva channels for the current effective team.
 * Team-scoped so switching teams (God Mode) triggers an automatic refetch.
 *
 * By default returns only active channels. Pass `{ includeInactive: true }` to
 * also fetch revoked/inactive channels (e.g. to power a reconnect prompt) — the
 * flag is included in the query key so active-only and full datasets are cached
 * separately. Existing callers (no args) keep active-only behavior unchanged.
 *
 * Pass `{ enabled: false }` to skip the request entirely (e.g. Canva is locked by the plan: a background
 * read would only produce a 403).
 */
export function useCanvaChannels(opts?: { includeInactive?: boolean; enabled?: boolean }) {
  const { teamId, hasTeam } = useEffectiveTeamId();
  const includeInactive = opts?.includeInactive ?? false;

  return useQuery({
    queryKey: CANVA_KEYS.channels(teamId, includeInactive),
    queryFn: () => canvaService.getChannels({ includeInactive }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: hasTeam && (opts?.enabled ?? true),
  });
}

/**
 * Hook to update the user-editable label on a Canva channel.
 * Invalidates the channels query on success so the list reflects the new name.
 */
export function useUpdateCanvaLabel() {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ channelId, label }: { channelId: number; label: string }) =>
      canvaService.updateChannelLabel(channelId, label),
    onSuccess: () => {
      // Invalidate both the Canva-specific channel list (import selector) —
      // partial key match so BOTH the active-only and includeInactive caches
      // are invalidated — and the general channels list (settings page via useChannels).
      queryClient.invalidateQueries({ queryKey: ['canva-channels', teamId] });
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
}

/**
 * Hook to browse Canva designs with infinite scroll
 */
export function useCanvaDesigns(params: {
  channelId: number | null;
  search?: string;
  sortBy?: 'modified_descending' | 'modified_ascending' | 'title_ascending' | 'title_descending';
}) {
  return useInfiniteQuery({
    queryKey: CANVA_KEYS.designs({
      channelId: params.channelId || 0,
      search: params.search,
      sortBy: params.sortBy,
    }),
    queryFn: async ({ pageParam }) => {
      const queryParams: ListDesignsParams = {
        channelId: params.channelId!,
        search: params.search,
        sortBy: params.sortBy,
        continuation: pageParam as string | undefined,
        limit: 24,
      };
      const response = await canvaService.listDesigns(queryParams);
      return response;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.continuation : undefined;
    },
    enabled: !!params.channelId,
    // Explicitly no placeholderData/keepPreviousData: a new search/sort key must
    // show the loading state, never the previous (unrelated) results.
  });
}

/**
 * Hook to start a Canva import
 */
export function useStartCanvaImport() {
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: (request: CanvaImportRequest) => canvaService.startImport(request),
    onSuccess: () => {
      toast.success(t('canva_import_started'));
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
}

/**
 * Hook to poll Canva import status
 */
export function useCanvaImportStatus(jobId: number | null) {
  return useQuery({
    queryKey: CANVA_KEYS.importStatus(jobId!),
    queryFn: () => canvaService.getImportStatus(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Stop polling when job is complete
      if (status && ['COMPLETED', 'FAILED', 'CANCELLED'].includes(status)) {
        return false; // Stop polling
      }
      return 2000; // Poll every 2 seconds
    },
    refetchIntervalInBackground: true,
  });
}

/**
 * Hook to cancel a Canva import
 */
export function useCancelCanvaImport() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: number) => canvaService.cancelImport(jobId),
    onSuccess: (_, jobId) => {
      toast.success(t('canva_import_cancelled'));
      queryClient.invalidateQueries({ queryKey: CANVA_KEYS.importStatus(jobId) });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
}
